import { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from "react-hook-form";
import { SelectChangeEvent, Autocomplete, TextField, Tooltip, Input, InputAdornment, Select, MenuItem, Button, IconButton, Checkbox, Popper, PopperProps } from "@mui/material";
import CustomSelect, { IOptions } from "../FormFields/CustomSelect";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { Refresh } from "@mui/icons-material";
import { fetchSuppliers } from '../../store/supplierStore/supplierThunk';
import { fetchCustomers } from '../../store/customerStore/customerThunk';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchLocations } from '../../store/locationStore/locationThunk';
import { makeStyles } from "@material-ui/core/styles";
import { fetchBuyerOrSupplierByName } from "../../store/accountThunk";
import { fetchTags } from "../../store/tagStore/tagThunk";
import { ITag, TagAppliesTo } from "../../utils/tag.types";

import { StyledStack } from './Filters.styles';
import { size } from 'lodash';
import { SearchIcon } from 'lucide-react';
import { VocuherCategory, VocuherTypes, ReportTypes, Utils } from '../../utils/common-utils';

import { apiLimit } from "../../utils/api.types";
import useModuleAccess from '../../hooks/useModuleAccess';

// Let the dropdown grow past the narrow input so long location names don't wrap.
const LocationPopper = (props: PopperProps) => (
  <Popper
    {...props}
    placement="bottom-start"
    style={{ ...props.style, width: 'max-content', minWidth: props.style?.width, maxWidth: 320 }}
  />
);

const useStyles = makeStyles({
  paper: {
    border: "1.5px solid #e9ecf1",
    boxShadow: 'none',
    borderRadius: 7
  },
  toggleButtonGroup: {
    width: '100%',
  },
  toggleButton: {
    width: 15,
    height: 15,
    marginBottom: 2
  },

});


interface FilterProps {
  type: string;
  filterHandler: (data: any) => void;
  selectedFilters?: (data: any) => void;
  setDefaultFilters?: any;
  hideFilter?: boolean ;
  hidePartySearch?: boolean;
  hideReset?: boolean;
  // Start with no date range ("Select Date") until one is picked.
  dateOnly?: boolean;
  // Opt-in: shows a location filter, but only when the account has more than one.
  showLocation?: boolean;
  // Opt out of remembering filters (e.g. the dashboard reuses the Sales type but must not share its saved filters).
  noPersist?: boolean;
  // Bump to reset the date filter back to the default range and location to all, from outside (e.g. a page refresh button).
  resetDateKey?: number;
  // Sit inside an existing filter row instead of taking the full width and forcing a wrap.
  inline?: boolean;
  // Page actions (e.g. Scan PI) rendered as the last item of the wrapping row.
  children?: ReactNode;
}

// Sales / Purchase listings remember their filters until Reset is clicked.
const PERSISTED_FILTER_TYPES = [VocuherCategory.SALE, VocuherCategory.PURCHASE];
// v2: bumped when the default range changed to Last 30 Days, so saved 'This FY Year' values are dropped.
const filterStorageKey = (type: string) => `ibook.listFilters.v2.${type}`;
const loadPersistedFilters = (type: string): any => {
  if (!PERSISTED_FILTER_TYPES.includes(type as any)) return null;
  try {
    const raw = localStorage.getItem(filterStorageKey(type));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

//filters component.
const Filters: FC<FilterProps> = ({ type, filterHandler, selectedFilters, setDefaultFilters, hideFilter, hidePartySearch, hideReset, dateOnly, showLocation, noPersist, resetDateKey, inline, children }) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { selectedAccount } = useAppSelector(state => state.auth);
  const { can } = useModuleAccess();
  var date = new Date();
  // const [startDate, setStartDate] = useState<Date>((type === 'dashboard') ? new Date(date.getFullYear(), date.getMonth(), 1) : new Date(Date.now() - 30 * 24 * 60 * 60 * 1000));
  const [startDate, setStartDate] = useState<Date>((type === 'dashboard') ? new Date(date.getFullYear(), date.getMonth(), 1) : new Date());
  const [endDate, setEndDate] = useState<Date>((type === 'dashboard') ? new Date(date.getFullYear(), date.getMonth() + 1, 0) : new Date());

  // Previously saved filters for this listing (null when nothing saved / not a persisted page).
  const [persisted] = useState<any>(() => noPersist ? null : loadPersistedFilters(type));

  const [selectedOption, setSelectedOption] = useState<string>(persisted?.selectedOption || '');
  const [filteroptions, setFilterOptions] = useState<IOptions[]>([]);
  const [options, setOptions] = useState<any[]>([]);
  const searchRequestRef = useRef(0);
  const { locations } = useAppSelector(state => state.locations);
  const [locationValue, setLocationValue] = useState<any>(null);
  const [selected, setSelected] = useState<any>(persisted?.selected || undefined);
  const [newOption, setNewOption] = useState("");
  const [closedSelect, setClosedSelect] = useState(false);
  const [statusSelected, setStatusSelected] = useState<string[]>(persisted?.statusSelected || ["all"]);
  const [statusoptions, setStatusoptions] = useState<IOptions[]>([]);
  // Tag filter: multi-select over the account's INVOICE tag master, filtered server-side.
  const [tagsSelected, setTagsSelected] = useState<string[]>(persisted?.tagsSelected || ["all"]);
  const [tagOptions, setTagOptions] = useState<ITag[]>([]);
  const [voucherOptions, setVoucherOptions] = useState<IOptions[]>([]);
  const [selectedDateRangeValue, setSelectedDateRangeValue] = useState(persisted?.selectedDateRangeValue || (dateOnly ? 'all' : "Last 30 Days"));
  const { startDateFY, endDateFY } = Utils.getFinancialYearDates();
  // Default range: the last 30 days, matching the 'Last 30 Days' option below.
  const startDateLast30 = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
  const endDateLast30 = new Date();
  const [dateRange, setDateRange] = useState({
    startDate: persisted?.dateRange?.startDate ? new Date(persisted.dateRange.startDate) : startDateLast30,
    endDate: persisted?.dateRange?.endDate ? new Date(persisted.dateRange.endDate) : endDateLast30
  });

  // Multi-select. A persisted value from before this was a plain string, so coerce.
  const [selectedVoucherType, setSelectedVoucherType] = useState<string[]>(() => {
    const raw = persisted?.selectedVoucherType ?? setDefaultFilters?.vType ?? 'all';
    if (Array.isArray(raw)) return raw.length ? raw : ['all'];
    return raw ? [String(raw)] : ['all'];
  });
  const [showDateRange, setShowDateRange] = useState<boolean>(persisted?.showDateRange || false);

  // Keep the saved copy in sync with whatever is currently applied.
  useEffect(() => {
    if (noPersist || !PERSISTED_FILTER_TYPES.includes(type as any)) return;
    try {
      localStorage.setItem(filterStorageKey(type), JSON.stringify({
        selectedOption,
        selected: selected ? { _id: selected._id, name: selected.name, whatsAppNumber: selected.whatsAppNumber } : null,
        statusSelected,
        tagsSelected,
        selectedDateRangeValue,
        dateRange: {
          startDate: dateRange.startDate ? new Date(dateRange.startDate).toISOString() : null,
          endDate: dateRange.endDate ? new Date(dateRange.endDate).toISOString() : null,
        },
        selectedVoucherType,
        showDateRange,
      }));
    } catch { /* storage unavailable — filters simply won't persist */ }
  }, [type, noPersist, selectedOption, selected, statusSelected, tagsSelected, selectedDateRangeValue, dateRange, selectedVoucherType, showDateRange]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // use form
  const methods = useForm<any>({});

  const formatDateToISO = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const createFilter = () => {
    let filter = (selectedOption && selectedOption !== 'all') ? `&filter=${selectedOption}` : '';
    // Multi-select voucher type: drop 'all', send as a comma-separated list.
    const voucherTypeValues = (selectedVoucherType || []).filter(v => v && v !== 'all');
    let voucherType = voucherTypeValues.length ? `&voucherType=${voucherTypeValues.join(',')}` : '';
    // Multi-select status: drop 'all', expand composite values, send as a comma-separated list.
    const statusValues = Array.from(
      new Set((statusSelected || []).filter(v => v && v !== 'all').flatMap(v => String(v).split(',')))
    ).filter(Boolean);
    let status = statusValues.length ? `&status=${statusValues.join(',')}` : '';
    // Multi-select tags: send the selected ids as a comma-separated list.
    const tagValues = (tagsSelected || []).filter(v => v && v !== 'all');
    let tagsParam = tagValues.length ? `&tags=${tagValues.join(',')}` : '';

    // Format dates to YYYY-MM-DD for server-side filtering
    const startDateStr = formatDateToISO(dateRange.startDate);
    const endDateStr = formatDateToISO(dateRange.endDate);
    // dateOnly + "Select Date" => emit NO range so the consumer applies no date filter.
    const noDate = dateOnly && (!selectedDateRangeValue || selectedDateRangeValue === 'all');
    const datePart = noDate ? '' : `start=${startDateStr}&end=${endDateStr}`;
    let locationParam = locationValue?.value ? `&locationId=${locationValue.value}` : '';
    let queryString = `${datePart}${filter}${voucherType}${status}${tagsParam}${locationParam}&_t=${Date.now()}`;
    filterHandler(queryString);

    let filterData = Object();
    if (selected && selectedFilters) { filterData.selectedClient = selected; }
    if (selectedFilters) { filterData.periodString = `Period: ${Utils.formatDate(dateRange.startDate)} - ${Utils.formatDate(dateRange.endDate)}`; }
    // Selected preset name for labelling the range; empty for Custom / Select Date.
    if (selectedFilters) {
      filterData.dateRangeLabel = (selectedDateRangeValue && selectedDateRangeValue !== 'Custom' && selectedDateRangeValue !== 'all')
        ? selectedDateRangeValue : '';
    }
    if (selectedFilters) { selectedFilters(filterData); }

  };

  const onResetClickHandler = () => {
    try { localStorage.removeItem(filterStorageKey(type)); } catch { /* ignore */ }
    setSelectedOption('');
    setSelected(null);
    setNewOption('');
    setStatusSelected(["all"]);
    setTagsSelected(["all"]);
    setSelectedVoucherType([type == VocuherCategory.SALE ? VocuherTypes.GST_INVOICE : VocuherTypes.PURCHASE_INVOICE]);
    setSelectedDateRangeValue('Last 30 Days');
    setDateRange({
      startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
      endDate: new Date()
    });
    setShowDateRange(false);
    setRefreshTrigger(prev => prev + 1);
  };

  const handleDateChange = (newValue: Date | null, name: string | undefined) => {
    if (newValue) {
      if (name === 'startDate') {
        const s = { ...dateRange };
        s.startDate = newValue;
        setDateRange(s);
      } else if (name === 'endDate') {
        const s = { ...dateRange };
        s.endDate = newValue;
        setDateRange(s);
      }
    }
  };

  // Locations drive the optional location filter; only fetched when asked for.
  useEffect(() => {
    if (showLocation) dispatch(fetchLocations());
  }, [dispatch, showLocation]);

  const locationOptions = useMemo(
    () => (locations || []).map((l: any) => ({ value: l._id, label: l.name })),
    [locations]
  );

  //Fetch all the suppliers/Buyers list on page load.
  // Disabled: the party dropdown loads from fetchAccountsByName as the user types, and filteroptions
  // is only read by the commented-out CustomSelect below, so this just pulled every party on load.
  // useEffect(() => {
  //   const getOptions = async () => {
  //     if (selectedAccount) {
  //       const { _id: id } = selectedAccount;
  //       const data: any = (type === 'account-payable') ? await dispatch(fetchSuppliers({ accountId: id, params: '' })) : await dispatch(fetchCustomers(id));
  //       const payload = Array.isArray(data?.payload) ? data.payload : [];
  //
  //       if (payload.length > 0) {
  //         let option = [];
  //         option = payload.map((item: any) => {
  //           return { label: item.name, value: item._id }
  //         });
  //         option.push({ label: 'All', value: 'all' })
  //         setFilterOptions(option)
  //       } else {
  //         setFilterOptions([]);
  //       }
  //     }
  //   }
  //   getOptions();
  // }, [dispatch, selectedAccount, type]);

  useEffect(() => {
    if (!resetDateKey) return;
    // A fresh dateRange object re-runs createFilter below even if the range is unchanged.
    setSelectedDateRangeValue('Last 30 Days');
    setShowDateRange(false);
    setDateRange({ startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), endDate: new Date() });
    setLocationValue(null);
  }, [resetDateKey]);

  useEffect(() => {
    if (!(dateRange.startDate && dateRange.startDate != undefined && dateRange.endDate && dateRange.endDate != undefined)) {
      return;
    }
    // Debounce so rapid filter changes fire a single API call.
    const timer = setTimeout(() => {
      createFilter();
    }, 500);
    return () => clearTimeout(timer);
  }, [dateRange, selectedVoucherType, selectedOption, statusSelected, tagsSelected, locationValue]);

  // Load the account's INVOICE tags once for the Tag filter.
  useEffect(() => {
    if (type !== VocuherCategory.SALE && type !== VocuherCategory.PURCHASE) return;
    let isMounted = true;
    dispatch(fetchTags(`appliesTo=${TagAppliesTo.INVOICE}&status=active`))
      .unwrap()
      .then((data: any) => { if (isMounted) setTagOptions(data || []); })
      .catch((error: any) => { console.error('Error fetching invoice tags:', error); });
    return () => { isMounted = false; };
  }, [dispatch, type, selectedAccount]);

  // Populate Status option on form load.
  useEffect(() => {
    let _statusOptions = [
      { label: 'All', value: 'all' },
      { label: "Draft", value: "DRAFT" },
      { label: "Pending", value: "PENDING" },
      { label: "Invoiced", value: "INVOICED" },
      { label: "Partially Paid", value: "PARTIALLY_PAID" },
      { label: "Paid", value: "PAID" },
      { label: "Over Paid", value: "OVER_PAID" },
      // Credit / debit note statuses
      { label: "Adjusted", value: "ADJUSTED" },
      { label: "Refunded", value: "REFUNDED" },
      { label: "Partially Refunded", value: "PARTIALLY_REFUNDED" },
      // { label: 'Invoice Sent', value: 'INVOICE_SENT' },
      { label: 'Outstanding', value: 'PENDING,PARTIALLY_PAID' }

    ]
    let _estimateStatusOptions = [
      { label: 'All', value: 'all' },
      { label: "Draft", value: "DRAFT" },
      { label: "Pending", value: "PENDING" },
      { label: "Accepted", value: "ACCEPTED" },
      { label: "Rejected", value: "REJECTED" },
    ]

    let _voucherTypeOptions;
    if (type == VocuherCategory.SALE) {
      _voucherTypeOptions = [
        { label: 'Select Voucher', value: 'all' },
        { label: VocuherTypes.GST_INVOICE, value: VocuherTypes.GST_INVOICE },
        { label: VocuherTypes.ESTIMATE, value: VocuherTypes.ESTIMATE },
        { label: VocuherTypes.CREDIT_NOTE, value: VocuherTypes.CREDIT_NOTE },
        { label: VocuherTypes.DELIVERY_NOTE, value: VocuherTypes.DELIVERY_NOTE },
        { label: VocuherTypes.PROFORMA_INVOICE, value: VocuherTypes.PROFORMA_INVOICE },
      ]
    } else {
      _voucherTypeOptions = [
        { label: 'All', value: 'all' },
        { label: VocuherTypes.PURCHASE_INVOICE, value: VocuherTypes.PURCHASE_INVOICE },
        { label: VocuherTypes.PURCHASE_ORDER, value: VocuherTypes.PURCHASE_ORDER },
        { label: VocuherTypes.DEBIT_NOTE, value: VocuherTypes.DEBIT_NOTE },
      ]
    }
    // Only voucher types the user can view.
    const voucherModule = type == VocuherCategory.SALE ? 'sales' : 'purchases';
    setVoucherOptions(_voucherTypeOptions.filter(o => o.value === 'all' || can(voucherModule, 'view', Utils.getInvoiceRouteName(o.value).routeName)))

    if (type === 'sale-estimate') {
      setStatusoptions(_estimateStatusOptions);
    } else {
      setStatusoptions(_statusOptions);
    }

  }, [can]);

  const onChangeHandler = (event: any, value: any) => {
    setNewOption('');
    // setIsLoading(true);
    setSelectedOption(value._id);
    setSelected(value);
  }

  // Multi-select status handler; 'All' is mutually exclusive with the rest.
  const handleStatusChange = (event: SelectChangeEvent<string[]>) => {
    const raw = event.target.value;
    const next = typeof raw === 'string' ? raw.split(',') : raw;
    const hadAll = statusSelected.includes('all');
    const hasAll = next.includes('all');
    if (hasAll && !hadAll) {
      // user just picked 'All'
      setStatusSelected(['all']);
    } else {
      const cleaned = next.filter(v => v !== 'all');
      setStatusSelected(cleaned.length ? cleaned : ['all']);
    }
  };

  // Label shown in the collapsed Select box.
  const renderStatusLabel = (vals: string[]) => {
    if (!vals || vals.length === 0 || (vals.length === 1 && vals[0] === 'all')) return 'All';
    return vals.map(v => statusoptions.find(o => o.value === v)?.label || v).join(', ');
  };

  // Multi-select tag handler — same 'All'-is-exclusive rule as status above.
  const handleTagChange = (event: SelectChangeEvent<string[]>) => {
    const raw = event.target.value;
    const next = typeof raw === 'string' ? raw.split(',') : raw;
    const hadAll = tagsSelected.includes('all');
    const hasAll = next.includes('all');
    if (hasAll && !hadAll) {
      setTagsSelected(['all']);
    } else {
      const cleaned = next.filter(v => v !== 'all');
      setTagsSelected(cleaned.length ? cleaned : ['all']);
    }
  };

  const renderTagLabel = (vals: string[]) => {
    if (!vals || vals.length === 0 || (vals.length === 1 && vals[0] === 'all')) return 'All Tags';
    return vals.map(v => tagOptions.find(t => t._id === v)?.name || v).join(', ');
  };


  const onInputChangeHandler = async (event: React.SyntheticEvent, value: string, reason?: string) => {
    // Ignore MUI's 'reset' so a restored party isn't cleared.
    if (reason === 'reset') return;
    setSelected('')
    setSelectedOption('');
    setNewOption(value);
    // Ignore replies to older searches.
    const requestId = ++searchRequestRef.current;
    if (value.trim().length < 3) return setOptions([]);
    await dispatch(fetchBuyerOrSupplierByName(`name=${encodeURIComponent(value)}&limit=20`)).unwrap().then((data) => {
      if (data && requestId === searchRequestRef.current) {
        setOptions(data.accounts);
      }
    });
  };

  // Match typed text against party name or WhatsApp number.
  const filterPartyOptions = (list: any[], state: { inputValue: string }) => {
    const query = state.inputValue.trim().toLowerCase();
    if (!query) return list;
    return list.filter(option =>
      String(option?.name || '').toLowerCase().includes(query) ||
      String(option?.whatsAppNumber || '').includes(query)
    );
  };

  const onCloseHandler = () => {
    setClosedSelect(true);
  };

  const onOpenHandler = () => {
    setClosedSelect(false);
  };

  // Multi-select voucher type — same 'All'-is-exclusive rule as status/tags above.
  const handleChangeVoucherType = (event: SelectChangeEvent<string[]>) => {
    const raw = event.target.value;
    const next = typeof raw === 'string' ? raw.split(',') : raw;
    const hadAll = selectedVoucherType.includes('all');
    const hasAll = next.includes('all');
    if (hasAll && !hadAll) {
      setSelectedVoucherType(['all']);
    } else {
      const cleaned = next.filter(v => v !== 'all');
      setSelectedVoucherType(cleaned.length ? cleaned : ['all']);
    }
  };

  const renderVoucherTypeLabel = (vals: string[]) => {
    const allLabel = voucherOptions.find(o => o.value === 'all')?.label || 'All';
    if (!vals || vals.length === 0 || (vals.length === 1 && vals[0] === 'all')) return allLabel;
    return vals.map(v => voucherOptions.find(o => o.value === v)?.label || v).join(', ');
  };

  const handleDateRange = (e: any) => {
    // console.log(e.target.value);
    var _dateFilterName = e.target.value;
    setSelectedDateRangeValue(_dateFilterName);
    // const _today = new Date();
    // const _quarter = Math.floor((_today.getMonth() / 3));
    // console.log(_quarter);

    // const startFullQuarter = new Date(today.getFullYear(), quarter * 3, 1);
    // const endFullQuarter = new Date(startFullQuarter.getFullYear(), startFullQuarter.getMonth() + 3, 0);

    if (e.target.value && e.target.value != '') {
      if (_dateFilterName === 'Today') {
        setShowDateRange(false);
        setDateRange({
          startDate: new Date(Date.now()),
          endDate: new Date(Date.now())
        })

      } else if (_dateFilterName === 'Yesterday') {
        setShowDateRange(false);
        setDateRange({
          startDate: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
          endDate: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000)
        });
      } else if (_dateFilterName === 'This Week') {
        setShowDateRange(false);
        var curr = new Date();
        setDateRange({
          // startDate: new Date(curr.setDate(curr.getDate() - curr.getDay())),
          // endDate: new Date(curr.setDate(curr.getDate() - curr.getDay()+6)),
          startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
          endDate: new Date(Date.now())
        });
      } else if (_dateFilterName === 'Last Week') {
        setShowDateRange(false);
        setDateRange({
          startDate: new Date(Date.now() - 13 * 24 * 60 * 60 * 1000),
          endDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
        });
      } else if (_dateFilterName === 'Last 30 Days') {
        setShowDateRange(false);
        setDateRange({
          startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
          endDate: new Date(Date.now())
        });
      } else if (_dateFilterName === 'This Month') {
        setShowDateRange(false);
        var date = new Date(), y = date.getFullYear(), m = date.getMonth();
        setDateRange({
          startDate: new Date(y, m, 1),
          endDate: new Date(y, m + 1, 0)
        });
      } else if (_dateFilterName === 'Last Month') {
        setShowDateRange(false);
        var date = new Date(), y = date.getFullYear(), m = date.getMonth();
        setDateRange({
          startDate: new Date(y, m - 1, 1),
          endDate: new Date(y, (m - 1) + 1, 0)
        });
      } else if (_dateFilterName === 'FY Q1') {
        setShowDateRange(false);
        const _quarter = 1;
        const qStartDate = new Date(startDateFY.getFullYear(), _quarter * 3, 1);
        const qEndDate = new Date(qStartDate.getFullYear(), qStartDate.getMonth() + 3, 0);

        setDateRange({
          startDate: qStartDate,
          endDate: qEndDate
        });
      } else if (_dateFilterName === 'FY Q2') {
        setShowDateRange(false);
        const _quarter = 2;
        const qStartDate = new Date(startDateFY.getFullYear(), _quarter * 3, 1);
        const qEndDate = new Date(qStartDate.getFullYear(), qStartDate.getMonth() + 3, 0);
        setDateRange({
          startDate: qStartDate,
          endDate: qEndDate
        });
      } else if (_dateFilterName === 'FY Q3') {
        setShowDateRange(false);
        const _quarter = 3;
        const qStartDate = new Date(startDateFY.getFullYear(), _quarter * 3, 1);
        const qEndDate = new Date(qStartDate.getFullYear(), qStartDate.getMonth() + 3, 0);
        setDateRange({
          startDate: qStartDate,
          endDate: qEndDate
        });
      } else if (_dateFilterName === 'FY Q4') {
        setShowDateRange(false);
        const _quarter = 0;
        const qStartDate = new Date(endDateFY.getFullYear(), _quarter * 3, 1);
        const qEndDate = new Date(qStartDate.getFullYear(), qStartDate.getMonth() + 2, 31);
        // Date.today().clearTime().moveToFirstDayOfMonth();
        setDateRange({
          startDate: qStartDate,
          endDate: qEndDate
        });
      } else if (_dateFilterName === 'This FY Year') {
        setShowDateRange(false);
        setDateRange({
          startDate: startDateFY,
          endDate: endDateFY
        });
      } else if (_dateFilterName === 'Last FY Year') {
        setShowDateRange(false);
        // var date = new Date(), y = date.getFullYear() - 1, m = date.getMonth();
        const { startDateLastFY, endDateLastFY } = Utils.getLastFinancialYearDates();
        setDateRange({
          startDate: startDateLastFY,
          endDate: endDateLastFY
        });
      } else if (_dateFilterName === 'Custom') {
        setShowDateRange(true);
        // Seed both start and end to today so the user picks a range from today's date.
        setDateRange({
          startDate: new Date(Date.now()),
          endDate: new Date(Date.now())
        });
      } else if (dateOnly) {
        setShowDateRange(false);
        setSelectedDateRangeValue('all');
      } else {
        setShowDateRange(false);
        setSelectedDateRangeValue('Last 30 Days');
        setDateRange({
          startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
          endDate: new Date()
        });
      }
    }
  };



  return (
    // Wrapping flex row so filters wrap cleanly at any width. Each control below
    // grows to share the free space on wide screens (flex basis + maxWidth cap)
    // instead of sitting at a fixed pixel width with dead space beside it.
    <div style={{
      display: 'flex',
      // Inline: stay on the host page's row instead of wrapping inside its narrow box.
      flexWrap: inline ? 'nowrap' : 'wrap',
      alignItems: 'center',
      rowGap: 10,
      columnGap: 10,
      width: inline ? 'auto' : '100%',
      flexShrink: inline ? 0 : undefined,
    }}>
      {/* {JSON.stringify(filteroptions)} */}
      {/* <StyledStack> */}
      {!hideReset && (
        <IconButton onClick={() => { onResetClickHandler(); }}
          style={{ color: '#488dff', backgroundColor: '#fff', border: '3px solid #e9ecf1', flexShrink: 0 }}
          size='small' ><Refresh /></IconButton>
      )}
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <FormProvider {...methods}>
          <>
            {!hidePartySearch && (
              <Autocomplete
                disablePortal
                id={`Search Party}`}
                // className='c-text-field-3'
                value={selected || null}
                style={{ flex: '2 1 230px', minWidth: 200, maxWidth: 420 }}
                onChange={onChangeHandler}
                onInputChange={onInputChangeHandler}
                onClose={onCloseHandler}
                onOpen={onOpenHandler}
                options={options}
                classes={{ paper: classes.paper }}
                getOptionLabel={option => option?.name || ''}
                filterOptions={filterPartyOptions}
                noOptionsText={String(newOption || '').trim().length < 3 ? 'Type at least 3 characters' : 'No party found'}
                isOptionEqualToValue={(option, value) => option?._id === value?._id}
                renderOption={(props, option) => (
                  <li {...props} key={option._id}>
                    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                      <span style={{ fontSize: 14 }}>{option.name}</span>
                      {option.whatsAppNumber && (
                        <span style={{ fontSize: 12, color: '#64748b' }}>{option.whatsAppNumber}</span>
                      )}
                    </div>
                  </li>
                )}
                renderInput={(params) => <TextField {...params}
                  // style={{width:300}}
                  className='c-text-field-3'
                  variant="standard"
                  name="search"
                  size='small'
                  placeholder={`Type here to search party ...`}
                  InputProps={{ ...params.InputProps, disableUnderline: true }}
                />}
              />
            )}
          </>

          {/* <TextField className='c-text-field-3' 
                variant="standard" 
                style={{width:300}}
                size='small'
                placeholder='Search Party, Invoice Number ...'
                InputProps={{
                disableUnderline: true,
                startAdornment: <InputAdornment position="start"><SearchIcon style={{marginTop:-3}} size={20}/></InputAdornment>,
                style:{ 
                  fontSize:14,
                  fontWeight: 'normal',
                },
                }}
                
              /> */}
          {/* {(type === VocuherCategory.SALE || type === VocuherCategory.PURCHASE) && filteroptions.length > 0 && <CustomSelect
              name="selectOption"
              label={(type === VocuherCategory.PURCHASE) ? "Supplier" : "Buyer"}
              id="selectOption"
              variant="standard"
              options={filteroptions}
              value={selectedOption}
              onChange={(event: SelectChangeEvent<any>) => { setSelectedOption(event.target.value) }} />} */}
          {!hideFilter && (type === VocuherCategory.SALE || type === VocuherCategory.PURCHASE) && statusoptions.length > 0 &&
            <>
              <Select
                name="status"
                disableUnderline
                MenuProps={{
                  PaperProps: {
                    sx: {
                      border: "1.5px solid #e9ecf1",
                      boxShadow: 'none',
                      borderRadius: '5px'
                    }
                  }
                }}
                sx={{
                  boxShadow: "none",
                  flex: '1 1 112px',
                  minWidth: 112,
                  maxWidth: 200,
                        pt: '0px !important',
                  pl: '10px !important',
                  '& .MuiOutlinedInput-input': { p: 0, m: 0 },
                  '& .MuiSelect-select': { p: 0 },
                  ".MuiOutlinedInput-notchedOutline": { border: 0 },
                  "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                  {
                    border: 0,
                  },
                  "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                  {
                    border: 0,
                  },
                }}
                label="Statuses"
                className='c-text-field-3'
                id="status"
                variant="outlined"
                multiple
                renderValue={(selectedVals) => renderStatusLabel(selectedVals as string[])}
                value={statusSelected}
                onChange={handleStatusChange} >
                {statusoptions.map(option => (
                  <MenuItem
                    value={option.value}
                    key={option.value}
                    disabled={option.disable || false}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 1 }}
                  >
                    <Checkbox
                      size="small"
                      checked={statusSelected.indexOf(option.value) > -1}
                      sx={{ p: 0.25, color: '#9da7b7', '&.Mui-checked': { color: '#16a34a' } }}
                    />
                    {option.label}
                  </MenuItem>
                ))}</Select>
            </>
          }


          <Select
            name="date"
            disableUnderline
            MenuProps={{
              PaperProps: {
                sx: {
                  border: "1.5px solid #e9ecf1",
                  boxShadow: 'none',
                  borderRadius: '5px'
                }
              }
            }}
            defaultValue={'all'}
            sx={{
              boxShadow: "none",
              flex: '1 1 170px',
              minWidth: 160,
              maxWidth: 260,
                pt: '0px !important',
              pl: '10px !important',
              '& .MuiOutlinedInput-input': { p: 0, m: 0 },
              '& .MuiSelect-select': { p: 0 },
              ".MuiOutlinedInput-notchedOutline": { border: 0 },
              "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
              {
                border: 0,
              },
              "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
              {
                border: 0,
              },
            }}
            label="Select Date"
            className='c-text-field-3'
            id="status"
            variant="outlined"
            value={selectedDateRangeValue || ''}
            onChange={(e) => { handleDateRange(e) }} >
            <MenuItem value={'all'} key={'all'}> Select Date</MenuItem>
            <MenuItem value={'Today'} key={'Today'}> Today </MenuItem>
            <MenuItem value={'Yesterday'} key={'Yesterday'}> Yesterday </MenuItem>
            <MenuItem value={'This Week'} key={'This Week'}> This Week </MenuItem>
            <MenuItem value={'Last Week'} key={'Last Week'}> Last Week </MenuItem>
            <MenuItem value={'Last 30 Days'} key={'Last 30 Days'}> Last 30 Days </MenuItem>
            <MenuItem value={'This Month'} key={'This Month'}> This Month </MenuItem>
            <MenuItem value={'Last Month'} key={'Last Month'}> Last Month </MenuItem>
            <MenuItem value={'FY Q1'} key={'FY Q1'}> FY Q1 (Apr - Jun) </MenuItem>
            <MenuItem value={'FY Q2'} key={'FY Q2'}> FY Q2 (Jul - Sept) </MenuItem>
            <MenuItem value={'FY Q3'} key={'FY Q3'}> FY Q3 (Oct - Dec) </MenuItem>
            <MenuItem value={'FY Q4'} key={'FY Q4'}> FY Q4 (Jan - Mar) </MenuItem>
            <MenuItem value={'This FY Year'} key={'This FY Year'}> This FY Year (Apr - Mar) </MenuItem>
            <MenuItem value={'Last FY Year'} key={'Last FY Year'} > Last FY Year (Apr - Mar) </MenuItem>
            <MenuItem value={'Custom'} key={'Custom'}> Custom Date </MenuItem>
          </Select>

          {/* Only worth showing when the account actually runs more than one location. */}
          {showLocation && locationOptions.length > 1 &&
            <Autocomplete
              id="locationFilterAutocomplete"
              disablePortal
              value={locationValue}
              options={locationOptions}
              getOptionLabel={(option: any) => option.label}
              isOptionEqualToValue={(option: any, value: any) => option.value === value?.value}
              onChange={(event, value) => setLocationValue(value)}
              renderOption={(props, option: any) => (
                <li {...props} style={{ fontSize: 14, minHeight: 36, whiteSpace: 'nowrap' }}>{option.label}</li>
              )}
              PopperComponent={LocationPopper}
              classes={{ paper: classes.paper }}
              style={{ flex: '1 1 140px', minWidth: 140, maxWidth: 240 }}
              renderInput={(params) => <TextField {...params}
                placeholder="Location"
                className='c-text-field-3'
                variant="standard"
                size='small'
                // c-text-field-3 adds top padding, which pushes the text below centre
                sx={{ paddingTop: '0 !important', justifyContent: 'center', '& .MuiInputBase-root': { alignItems: 'center', py: 0 }, '& input': { cursor: 'pointer', caretColor: 'transparent' } }}
                InputProps={{ ...params.InputProps, disableUnderline: true }}
                // Pick-only: locations are a short list, so no typing — a click opens the list.
                inputProps={{ ...params.inputProps, readOnly: true }}
              />}
            />
          }

          {!hideFilter && (type === VocuherCategory.SALE || type === VocuherCategory.PURCHASE) && statusoptions.length > 0 &&
            <>

              <Select
                name="voucherType"
                disableUnderline
                MenuProps={{
                  PaperProps: {
                    sx: {
                      border: "1.5px solid #e9ecf1",
                      boxShadow: 'none',
                      borderRadius: '5px'
                    }
                  }
                }}
                sx={{
                  boxShadow: "none",
                  flex: '1 1 152px',
                  minWidth: 150,
                  maxWidth: 240,
                        pt: '0px !important',
                  pl: '10px !important',
                  '& .MuiOutlinedInput-input': { p: 0, m: 0 },
                  '& .MuiSelect-select': { p: 0 },
                  ".MuiOutlinedInput-notchedOutline": { border: 0 },
                  "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                  {
                    border: 0,
                  },
                  "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                  {
                    border: 0,
                  },
                }}
                label="Voucher Type"
                className='c-text-field-3'
                id="voucherType"
                variant="outlined"
                multiple
                displayEmpty
                renderValue={(selectedVals) => renderVoucherTypeLabel(selectedVals as string[])}
                value={selectedVoucherType}
                onChange={handleChangeVoucherType} >
                {voucherOptions.map(option => (
                  <MenuItem
                    value={option.value}
                    key={option.value}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 1 }}
                  >
                    <Checkbox
                      size="small"
                      checked={selectedVoucherType.indexOf(option.value) > -1}
                      sx={{ p: 0.25, color: '#9da7b7', '&.Mui-checked': { color: '#16a34a' } }}
                    />
                    {option.label}
                  </MenuItem>
                ))}
                {/* { type == VocuherCategory.SALE &&
                  <>
                  <MenuItem value={'all'} key={'all'} > Select Voucher {selectedVoucherType}</MenuItem>
                  <MenuItem value={'Invoice'} key={'Invoice'} selected> Invoice </MenuItem>
                  <MenuItem value={'Credit Note'} key={'Credit Note'}> Credit Note </MenuItem>
                  <MenuItem value={'Quotation'} key={'Quotation'}> Quotation </MenuItem>
                  <MenuItem value={'Delivery Note'} key={'Delivery Note'}> Delivery Note </MenuItem>
                  <MenuItem value={'Proforma Invoice'} key={'Proforma Invoice'}>Proforma Invoice </MenuItem>
                  </>
                  }
                  { type == VocuherCategory.PURCHASE &&
                  <>
                  <MenuItem value={'all'} key={'all'} > Select Voucher</MenuItem>
                  <MenuItem value={'Purchase Order'} key={'Purchase Order'}> Purchase Order </MenuItem>
                  <MenuItem value={'Purchase Invoice'} key={'Purchase Invoice'} selected> Purchase Invoice </MenuItem>
                  </>
                  } */}
              </Select>

              {/* Tag filter — multi-select, filtered server-side. */}
              {tagOptions.length > 0 &&
                <Select
                  name="tags"
                  disableUnderline
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        border: "1.5px solid #e9ecf1",
                        boxShadow: 'none',
                        borderRadius: '5px'
                      }
                    }
                  }}
                  sx={{
                    boxShadow: "none",
                    flex: '1 1 100px',
                    minWidth: 100,
                    maxWidth: 180,
                            pt: '0px !important',
                    pl: '10px !important',
                    '& .MuiOutlinedInput-input': { p: 0, m: 0 },
                    '& .MuiSelect-select': { p: 0 },
                    ".MuiOutlinedInput-notchedOutline": { border: 0 },
                    "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                    {
                      border: 0,
                    },
                    "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                    {
                      border: 0,
                    },
                  }}
                  label="Tags"
                  className='c-text-field-3'
                  id="tags"
                  variant="outlined"
                  multiple
                  displayEmpty
                  renderValue={(selectedVals) => renderTagLabel(selectedVals as string[])}
                  value={tagsSelected}
                  onChange={handleTagChange} >
                  <MenuItem value={'all'} key={'all'}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 1 }}>
                    <Checkbox
                      size="small"
                      checked={tagsSelected.indexOf('all') > -1}
                      sx={{ p: 0.25, color: '#9da7b7', '&.Mui-checked': { color: '#16a34a' } }}
                    />
                    All Tags
                  </MenuItem>
                  {tagOptions.map(tag => (
                    <MenuItem
                      value={tag._id}
                      key={tag._id}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 1 }}
                    >
                      <Checkbox
                        size="small"
                        checked={tagsSelected.indexOf(tag._id) > -1}
                        sx={{ p: 0.25, color: '#9da7b7', '&.Mui-checked': { color: '#16a34a' } }}
                      />
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{
                          width: 10, height: 10, borderRadius: '50%',
                          backgroundColor: tag.color || '#7f8c9b',
                          display: 'inline-block', flexShrink: 0
                        }} />
                        {tag.name}
                      </span>
                    </MenuItem>
                  ))}</Select>
              }

            </>
          }
          {showDateRange &&
            // Keep Start and End dates together when wrapping.
            <div className="filters-custom-dates" style={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'nowrap',
              gap: 8,
              flexShrink: 0,
            }}>
              <DesktopDatePicker
                inputFormat="dd/MM/yyyy"
                InputProps={{
                  disableUnderline: true,
                  style: {
                    fontSize: 14,
                    height: 28
                  },
                }}
                renderInput={(params) =>
                  <TextField {...params}
                    required
                    className='c-text-field-3'
                    variant="standard"
                    sx={{ flex: '1 1 150px', minWidth: 150, maxWidth: 220 }}
                    size='small'
                    placeholder="Start Date"

                  />}
                value={dateRange.startDate || 0}
                onChange={(value) => { handleDateChange(value, 'startDate') }}
              />
              <DesktopDatePicker
                inputFormat="dd/MM/yyyy"
                InputProps={{
                  disableUnderline: true,
                  style: {
                    fontSize: 14,
                    height: 28
                  },
                }}
                renderInput={(params) =>
                  <TextField {...params}
                    required
                    className='c-text-field-3'
                    variant="standard"
                    sx={{ flex: '1 1 150px', minWidth: 150, maxWidth: 220 }}
                    size='small'
                    placeholder="End Date"

                  />}
                value={dateRange.endDate || 0}
                onChange={(value) => { handleDateChange(value, 'endDate') }}
              />
            </div>
          }
        </FormProvider>
      </LocalizationProvider>
      {/* Rendered inside the wrapping row so page actions flow after the last filter.
          marginLeft:auto keeps them pinned right once the filters stop growing. */}
      {children && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginLeft: 'auto', flexShrink: 0 }}>
          {children}
        </div>
      )}
      {/* </StyledStack> */}
    </div>
  );
}

export default Filters;
